import React, { useState, useCallback, useMemo, memo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

// Types for better type safety
interface NewsletterState {
  email: string;
  isSubmitting: boolean;
  message: {
    type: "success" | "error" | null;
    text: string;
  };
}

interface NewsLetterProps {
  title?: string;
  subtitle?: string;
  placeholder?: string;
  buttonText?: string;
  onSubscribe?: (email: string) => Promise<void> | void;
  className?: string;
  showPrivacyNote?: boolean;
  backgroundGradient?: string;
}

const NewsLetter: React.FC<NewsLetterProps> = memo(
  ({
    title,
    subtitle,
    placeholder,
    buttonText,
    onSubscribe,
    className = "",
    showPrivacyNote = true,
    backgroundGradient,
  }) => {
    const { t } = useTranslation();
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
      setIsLoaded(true);
    }, []);

    // Centralized state management
    const [state, setState] = useState<NewsletterState>({
      email: "",
      isSubmitting: false,
      message: { type: null, text: "" },
    });

    // Memoized validation function
    const validateEmail = useCallback((email: string): boolean => {
      const emailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      return emailRegex.test(email.trim());
    }, []);

    // Memoized form validation
    const isFormValid = useMemo(() => {
      const trimmedEmail = state.email.trim();
      return trimmedEmail.length > 0 && validateEmail(trimmedEmail);
    }, [state.email, validateEmail]);

    // Optimized input change handler
    const handleEmailChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const email = e.target.value;
        setState((prev) => ({
          ...prev,
          email,
          message: { type: null, text: "" },
        }));
      },
      []
    );

    // Enhanced form submission with error handling
    const handleSubmit = useCallback(
      async (e: React.FormEvent) => {
        e.preventDefault();

        const trimmedEmail = state.email.trim();

        if (!trimmedEmail) {
          setState((prev) => ({
            ...prev,
            message: {
              type: "error",
              text: t("newsletter.emailRequired"),
            },
          }));
          return;
        }

        if (!validateEmail(trimmedEmail)) {
          setState((prev) => ({
            ...prev,
            message: {
              type: "error",
              text: t("newsletter.emailInvalid"),
            },
          }));
          return;
        }

        setState((prev) => ({ ...prev, isSubmitting: true }));

        try {
          await onSubscribe?.(trimmedEmail);

          setState({
            email: "",
            isSubmitting: false,
            message: {
              type: "success",
              text: t("newsletter.subscribeSuccess"),
            },
          });

          setTimeout(() => {
            setState((prev) => ({
              ...prev,
              message: { type: null, text: "" },
            }));
          }, 5000);
        } catch {
          setState((prev) => ({
            ...prev,
            isSubmitting: false,
            message: {
              type: "error",
              text: t("newsletter.subscribeError"),
            },
          }));
        }
      },
      [state.email, onSubscribe, validateEmail, t]
    );

    const defaultGradient =
      backgroundGradient ||
      "bg-gradient-to-br from-stone-50/80 via-emerald-50/60 to-stone-50/80 dark:from-gray-800/80 dark:via-stone-900/20 dark:to-emerald-900/20";

    return (
      <section className={`relative py-24 px-4 sm:px-6 lg:px-8 overflow-hidden ${className}`}>
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-stone-200/20 dark:bg-stone-900/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-200/20 dark:bg-emerald-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto">
          {/* Newsletter Container */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.8 }}
            className={`${defaultGradient} backdrop-blur-md rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden`}
          >
            <div className="px-6 py-12 sm:px-8 sm:py-16 lg:px-12 lg:py-20">
              {/* Content Container */}
              <div className="max-w-2xl mx-auto text-center">
                {/* Icon */}
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={isLoaded ? { scale: 1, rotate: 0 } : { scale: 0, rotate: -180 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-emerald-500 to-stone-600 rounded-2xl mb-6 shadow-xl"
                >
                  <svg
                    className="w-10 h-10 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </motion.div>

                {/* Title */}
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-white mb-4"
                >
                  {title || t("newsletter.title")}
                  <span className="block mt-2 text-2xl sm:text-3xl lg:text-4xl bg-gradient-to-r from-emerald-600 to-stone-600 bg-clip-text text-transparent">
                    {t("newsletter.titleAccent", "Stay Connected")}
                  </span>
                </motion.h2>

                {/* Subtitle */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.5 }}
                  className="text-lg md:text-xl text-gray-600 dark:text-gray-300 mb-10 leading-relaxed"
                >
                  {subtitle || t("newsletter.subtitle")}
                </motion.p>

                {/* Newsletter Form */}
                <motion.form
                  initial={{ opacity: 0, y: 20 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  onSubmit={handleSubmit}
                  className="space-y-6"
                >
                  <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                    {/* Email Input */}
                    <div className="flex-1 relative">
                      <input
                        type="email"
                        value={state.email}
                        onChange={handleEmailChange}
                        placeholder={placeholder || t("newsletter.placeholder")}
                        disabled={state.isSubmitting}
                        className={`
                        w-full px-5 py-4 rounded-2xl border-2 transition-all duration-200
                        bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm text-gray-900 dark:text-white
                        placeholder-gray-500 dark:placeholder-gray-400
                        focus:outline-none focus:ring-4 focus:ring-blue-500/50 focus:border-blue-500
                        disabled:opacity-50 disabled:cursor-not-allowed font-medium
                        ${state.message.type === "error"
                            ? "border-red-300 focus:ring-red-500/50"
                            : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                          }
                      `}
                        aria-label={t("newsletter.placeholder")}
                        aria-describedby={
                          state.message.text ? "newsletter-message" : undefined
                        }
                        required
                      />

                      {/* Loading Spinner */}
                      <AnimatePresence>
                        {state.isSubmitting && (
                          <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="absolute right-4 top-1/2 transform -translate-y-1/2"
                          >
                            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Submit Button */}
                    <motion.button
                      type="submit"
                      disabled={!isFormValid || state.isSubmitting}
                      whileHover={
                        isFormValid && !state.isSubmitting
                          ? { scale: 1.05, y: -2 }
                          : {}
                      }
                      whileTap={
                        isFormValid && !state.isSubmitting ? { scale: 0.95 } : {}
                      }
                      className={`
                      px-8 py-4 rounded-2xl font-bold text-white transition-all duration-200
                      focus:outline-none focus:ring-4 focus:ring-blue-500/50
                      disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none
                      ${isFormValid && !state.isSubmitting
                          ? "bg-gradient-to-r from-emerald-600 to-stone-600 hover:from-emerald-700 hover:to-stone-700 shadow-xl hover:shadow-2xl"
                          : "bg-gray-400 cursor-not-allowed"
                        }
                    `}
                    >
                      {state.isSubmitting
                        ? t("newsletter.buttonText") + "..."
                        : buttonText || t("newsletter.buttonText")}
                    </motion.button>
                  </div>

                  {/* Message Display */}
                  <AnimatePresence>
                    {state.message.type && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        id="newsletter-message"
                        className={`text-center p-4 rounded-xl font-semibold ${state.message.type === "success"
                          ? "bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border-2 border-green-200 dark:border-green-800"
                          : "bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border-2 border-red-200 dark:border-red-800"
                          }`}
                        role={
                          state.message.type === "error" ? "alert" : "status"
                        }
                      >
                        {state.message.text}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Privacy Note */}
                  {showPrivacyNote && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={isLoaded ? { opacity: 1 } : { opacity: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 }}
                      className="text-sm text-gray-500 dark:text-gray-400 text-center"
                    >
                      {t("newsletter.privacyNote")}{" "}
                      <button
                        type="button"
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded"
                      >
                        {t("newsletter.unsubscribe")}
                      </button>
                    </motion.p>
                  )}
                </motion.form>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }
);

// Display name for debugging
NewsLetter.displayName = "NewsLetter";

export default NewsLetter;